/**
 * The app reads the combined session-list composition needs, named once.
 *
 * `useQueryCombinedSessions` — the composition over the v1/v2/v3 list queries
 * and the offline-recording union on top of them — now lives in
 * `@heidi/session/data`. It cannot perform these reads itself: two are
 * OpenFeature flag reads, one wraps `isTauri()` plus the native reachability
 * atom, one selects over a foreign domain's user query, and two are app atoms,
 * which never cross into a package as imports. So they are resolved here and
 * injected as ONE required `configureSessionData` member
 * (`src/utils/session-data-wiring.ts`), which is ruling 32's shape.
 *
 * THIS HOOK STAYS APP-SIDE BY DESIGN while offline recording has no package of
 * its own and the flags have no door. It is not a shim awaiting a move: the
 * package's business is what it does with these values, not where they come
 * from.
 *
 * THE EXPRESSIONS ARE VERBATIM from the composition this replaces
 * (`src/hooks/query/useQueryCombinedSessions.ts`), including the read order.
 * `isUserLoaded` joined them later, quoted from `useQuerySessionsList`'s own
 * readiness gate, for the recent-engagement read that shares its cache entry.
 */
import {
  offlineLocalSessionsAtom,
  offlineSyncQueueAtom,
} from '@/atoms/offline-recording';
import {
  useIsNewSessionListEnabled,
  useIsOfflineRecordingEnabled,
} from '@/features/flags';
import { useIsDesktopOnlineReady } from '@/hooks/desktop/useIsDesktopOnlineReady';
import { type SessionListEnvironment } from '@heidi/session/data';
import { useUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';

export function useSessionListEnvironment(): SessionListEnvironment {
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const isDesktopOnlineReady = useIsDesktopOnlineReady();
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  // A presence check, not an identity: the list request identifies the caller
  // from the bearer token, and `kinde_user_id` can be empty on a loaded user.
  // Selecting a stable boolean keeps unrelated user-object changes from
  // re-rendering every consumer of this environment.
  const { data: isUserLoaded } = useUser((data) => data != null);
  const offlineLocalSessions = useAtomValue(offlineLocalSessionsAtom);
  const offlineSyncQueue = useAtomValue(offlineSyncQueueAtom);

  // Not memoized, and it does not need to be: the package hook destructures
  // this object on the line it calls, so its identity reaches nothing. What
  // DOES reach a dependency list is the two arrays, and they cross by
  // reference straight from the atoms — so the offline union re-runs on
  // exactly the renders it re-ran on when the composition read the atoms
  // itself.
  return {
    isNewSessionListEnabled,
    isOfflineRecordingEnabled,
    isDesktopOnlineReady,
    isUserLoaded: Boolean(isUserLoaded),
    offlineLocalSessions,
    offlineSyncQueue,
  };
}
